import { fmtSigned, type CoachTrend as TrendData } from '../lib/api';

/**
 * A coach season by season, so a direction shows rather than a snapshot.
 *
 * One season says he gave away three points of win probability a game. Five say whether
 * he is learning, which is the question a fan actually has. A season still being played is
 * drawn faintly and marked: two games of 2026 were enough to reverse a five-year direction,
 * so it is shown without being allowed to set one.
 */

const DIRECTION: Record<string, { label: string; cls: string }> = {
  improving: { label: 'Improving', cls: 'bg-greenTint text-green' },
  declining: { label: 'Declining', cls: 'bg-redTint text-red' },
  steady: { label: 'Steady', cls: 'bg-blueTint text-blue' },
};

export function CoachTrendChart({ data }: { data: TrendData }) {
  const priced = data.seasons.filter((s) => s.wp_impact_per_game_pct !== null);
  if (!priced.length) return null;

  // all costs are negative, so the worst season sets the scale
  const worst = Math.min(...priced.map((s) => s.wp_impact_per_game_pct as number));
  const scale = Math.abs(worst) || 1;
  const badge = data.direction ? DIRECTION[data.direction] : null;

  return (
    <section>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <p className="kicker mb-1">Season by season</p>
          <h3 className="h3">Is he getting better?</h3>
        </div>
        {badge && (
          <span className={`pill ${badge.cls}`}>
            {badge.label}
            {data.change_per_game_pct !== null && ` ${fmtSigned(data.change_per_game_pct, 2, '%')}`}
          </span>
        )}
      </div>

      <p className="body-muted mt-1 max-w-[56ch] text-[14px]">
        Win probability given away per game, on his own calls. Less is better.
        {data.direction_from && data.direction_from.length > 1 &&
          ` The direction is measured across ${data.direction_from[0]} to ${data.direction_from[data.direction_from.length - 1]}.`}
      </p>

      <ol className="mt-4 flex flex-col gap-2.5">
        {data.seasons.map((s) => {
          const cost = s.wp_impact_per_game_pct;
          const width = cost === null ? 0 : Math.min(100, (Math.abs(cost) / scale) * 100);
          return (
            <li key={s.season} className="flex items-center gap-3">
              <span className={`num w-12 shrink-0 text-[13px] ${s.partial ? 'text-faint' : 'text-muted'}`}>{s.season}</span>
              <span className="h-5 min-w-0 flex-1 overflow-hidden rounded-sm bg-wash">
                <span
                  className={`block h-full ${s.partial ? 'bg-blue/30' : 'bg-blue'}`}
                  style={{ width: `${width}%` }}
                  title={`${s.decisions} calls over ${s.games} games`}
                />
              </span>
              <span className={`num w-16 shrink-0 text-right text-[13px] ${s.partial ? 'text-faint' : ''}`}>
                {cost === null ? '-' : `${cost.toFixed(2)}%`}
              </span>
              <span className="w-20 shrink-0 text-right text-[11px] text-faint">
                {s.partial ? `${s.games} so far` : `${s.games} games`}
              </span>
            </li>
          );
        })}
      </ol>

      {data.seasons.some((s) => s.partial) && (
        <p className="mt-3 text-[12px] text-faint">
          The faded season is still being played, so it is shown but does not set the direction.
        </p>
      )}
    </section>
  );
}
